import React, { useMemo } from 'react';

import waveLengthRange from '@client/config/waveLengthRange';
import i18n from '@client/utils/i18n';
import wavelengthToColor from '@client/utils/wavelengthToColor';
import { SpectrogramPoint } from '@ledium/types';
import { useTheme } from '@mui/material/styles';
import { max } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import { area, curveMonotoneX } from 'd3-shape';

interface WavelengthDataPoint extends SpectrogramPoint {
    targetIrradiance?: number;
    targetPfd?: number;
}

interface WavelengthChartProps {
    data: WavelengthDataPoint[];
    unit?: string;
    small?: boolean;
    aspect?: number;
    animation?: number;
    yAxis?: number;
    showLegend?: boolean;
}

const GRADIENT_ID = `spectrum-gradient-${waveLengthRange.min}-${waveLengthRange.max}`;
const BASE_WIDTH = 600;
const NORMAL_MARGIN = { top: 10, right: 15, bottom: 50, left: 65 };
const SMALL_MARGIN = { top: 2, right: 2, bottom: 2, left: 2 };
const TICK_SIZE = 5;

const gradientStops = [...Array(waveLengthRange.stepNum).keys()].map((i) => ({
    offset: `${(i / waveLengthRange.stepNum) * 100}%`,
    color: wavelengthToColor(i * waveLengthRange.step + waveLengthRange.min)
}));

const tickFormatter = (v: number) => parseFloat(v.toPrecision(2)).toString();

const getValue = (d: WavelengthDataPoint, key: string): number | undefined => {
    if (key === 'irradiance') {
        return d.irradiance;
    }
    if (key === 'pfd') {
        return d.pfd;
    }
    if (key === 'targetIrradiance') {
        return d.targetIrradiance;
    }
    if (key === 'targetPfd') {
        return d.targetPfd;
    }
    return undefined;
};

const WavelengthChart = ({ data, unit = 'irradiance', small = false, aspect = 3, yAxis, showLegend = false }: WavelengthChartProps) => {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = React.useState(BASE_WIDTH);

    React.useEffect(() => {
        const el = containerRef.current;
        if (!el) {
            return;
        }
        const observer = new ResizeObserver(([entry]) => {
            setContainerWidth(entry.contentRect.width);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Keep axis font size constant in screen pixels regardless of rendered chart width
    const fontSize = Math.round(14 * (BASE_WIDTH / Math.max(containerWidth, 1)));

    const margin = small ? SMALL_MARGIN : NORMAL_MARGIN;
    const totalWidth = BASE_WIDTH;
    const totalHeight = Math.round(BASE_WIDTH / aspect);
    const innerWidth = totalWidth - margin.left - margin.right;
    const innerHeight = totalHeight - margin.top - margin.bottom;

    const targetKey = `target${unit.charAt(0).toUpperCase() + unit.slice(1)}`;
    const hasTarget = data.some((d) => getValue(d, targetKey) !== undefined);

    const xScale = useMemo(() => scaleLinear().domain([waveLengthRange.min, waveLengthRange.max]).range([0, innerWidth]), [innerWidth]);

    const yDomainMax = useMemo(() => {
        const mainMax = max(data, (d) => getValue(d, unit)) ?? 0;
        const targetMax = hasTarget ? (max(data, (d) => getValue(d, targetKey)) ?? 0) : 0;
        const dataMax = Math.max(mainMax, targetMax);
        return dataMax > 0 ? dataMax : 1;
    }, [data, unit, hasTarget, targetKey]);

    const yScale = useMemo(() => {
        const scale = scaleLinear().range([innerHeight, 0]);
        if (yAxis === undefined) {
            scale.domain([0, yDomainMax]).nice();
        } else {
            scale.domain([0, yAxis]);
        }
        return scale;
    }, [yAxis, yDomainMax, innerHeight]);

    const areaPath = useMemo(() => {
        const gen = area<WavelengthDataPoint>()
            .x((d) => xScale(d.waveLength))
            .y0(innerHeight)
            .y1((d) => yScale(getValue(d, unit) ?? 0))
            .curve(curveMonotoneX);
        return gen(data) ?? '';
    }, [data, xScale, yScale, unit, innerHeight]);

    const targetAreaPath = useMemo(() => {
        if (!hasTarget) {
            return '';
        }
        const gen = area<WavelengthDataPoint>()
            .x((d) => xScale(d.waveLength))
            .y0(innerHeight)
            .y1((d) => yScale(getValue(d, targetKey) ?? 0))
            .defined((d) => getValue(d, targetKey) !== undefined)
            .curve(curveMonotoneX);
        return gen(data) ?? '';
    }, [data, xScale, yScale, targetKey, hasTarget, innerHeight]);

    const xTicks = useMemo(() => xScale.ticks(10), [xScale]);
    const yTicks = useMemo(() => yScale.ticks(5), [yScale]);

    return (
        <div
            ref={containerRef}
            style={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.07)' : theme.palette.divider}` }}
        >
            <svg viewBox={`0 0 ${totalWidth} ${totalHeight}`} width="100%" style={{ display: 'block', userSelect: 'none' }}>
                <defs>
                    <linearGradient id={GRADIENT_ID} x1="0" y1="0" x2="1" y2="0">
                        {gradientStops.map((s, i) => (
                            <stop key={i} offset={s.offset} stopColor={s.color} />
                        ))}
                    </linearGradient>
                </defs>

                <g transform={`translate(${margin.left},${margin.top})`}>
                    {!small && (
                        <>
                            {yTicks.map((tick) => (
                                <line
                                    key={`gy-${tick}`}
                                    x1={0}
                                    x2={innerWidth}
                                    y1={yScale(tick)}
                                    y2={yScale(tick)}
                                    stroke={theme.palette.divider}
                                    strokeDasharray="3 3"
                                />
                            ))}
                            {xTicks.map((tick) => (
                                <line
                                    key={`gx-${tick}`}
                                    x1={xScale(tick)}
                                    x2={xScale(tick)}
                                    y1={0}
                                    y2={innerHeight}
                                    stroke={theme.palette.divider}
                                    strokeDasharray="3 3"
                                />
                            ))}
                        </>
                    )}

                    <path d={areaPath} fill={`url(#${GRADIENT_ID})`} fillOpacity={1} stroke={dark ? 'none' : theme.palette.text.disabled} strokeWidth={1} />

                    {hasTarget && (
                        <path
                            d={targetAreaPath}
                            fill={`url(#${GRADIENT_ID})`}
                            fillOpacity={0.15}
                            stroke={dark ? 'none' : theme.palette.divider}
                            strokeWidth={1}
                            strokeDasharray="5 4"
                        />
                    )}

                    {small && (
                        <>
                            <line x1={0} x2={innerWidth} y1={innerHeight} y2={innerHeight} stroke={theme.palette.divider} strokeWidth={1} />
                            <line x1={0} x2={0} y1={0} y2={innerHeight} stroke={theme.palette.divider} strokeWidth={1} />
                            <line x1={0} x2={innerWidth} y1={0} y2={0} stroke={theme.palette.divider} strokeWidth={1} />
                            <line x1={innerWidth} x2={innerWidth} y1={0} y2={innerHeight} stroke={theme.palette.divider} strokeWidth={1} />
                        </>
                    )}

                    {!small && (
                        <>
                            <g transform={`translate(0,${innerHeight})`}>
                                <line x1={0} x2={innerWidth} stroke={theme.palette.text.disabled} />
                                {xTicks.map((tick) => (
                                    <g key={tick} transform={`translate(${xScale(tick)},0)`}>
                                        <line y2={TICK_SIZE} stroke={theme.palette.text.disabled} />
                                        <text y={TICK_SIZE + 4} dy="0.71em" textAnchor="middle" fontSize={fontSize} fill={theme.palette.text.secondary}>
                                            {tick}
                                        </text>
                                    </g>
                                ))}
                            </g>

                            <g>
                                <line y1={0} y2={innerHeight} stroke={theme.palette.text.disabled} />
                                {yTicks.map((tick) => (
                                    <g key={tick} transform={`translate(0,${yScale(tick)})`}>
                                        <line x2={-TICK_SIZE} stroke={theme.palette.text.disabled} />
                                        <text x={-TICK_SIZE - 3} dy="0.32em" textAnchor="end" fontSize={fontSize} fill={theme.palette.text.secondary}>
                                            {tickFormatter(tick)}
                                        </text>
                                    </g>
                                ))}
                                <text
                                    transform={`translate(${-(margin.left - 22)},${innerHeight / 2}) rotate(-90)`}
                                    textAnchor="middle"
                                    fontSize={fontSize}
                                    fill={theme.palette.text.secondary}
                                >
                                    {`${i18n(unit) as string} (${i18n(unit + '_unit') as string})`}
                                </text>
                            </g>

                            {showLegend && (
                                <g transform={`translate(0,${innerHeight + 35})`}>
                                    <rect
                                        x={0}
                                        y={-6}
                                        width={12}
                                        height={12}
                                        fill={`url(#${GRADIENT_ID})`}
                                        stroke={dark ? 'none' : theme.palette.text.disabled}
                                        strokeWidth={1}
                                    />
                                    <text x={16} dy="0.32em" fontSize={fontSize} fill={theme.palette.text.secondary}>
                                        {i18n('wave_length_intensity') as string}
                                    </text>
                                    {hasTarget && (
                                        <>
                                            <rect
                                                x={200}
                                                y={-6}
                                                width={12}
                                                height={12}
                                                fill={`url(#${GRADIENT_ID})`}
                                                fillOpacity={0.15}
                                                stroke={dark ? 'none' : theme.palette.divider}
                                                strokeWidth={1}
                                                strokeDasharray="3 2"
                                            />
                                            <text x={216} dy="0.32em" fontSize={fontSize} fill={theme.palette.text.secondary}>
                                                {i18n('target_wave_length_intensity') as string}
                                            </text>
                                        </>
                                    )}
                                </g>
                            )}
                        </>
                    )}
                </g>
            </svg>
        </div>
    );
};

export default React.memo(WavelengthChart);
